import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronRight, ArrowLeft, X, ChevronLeft } from "lucide-react";

const toGallery = (entry) => {
  if (!entry) return [];
  return Array.isArray(entry) ? entry.filter(Boolean) : [entry];
};

export const FeatureExplorer = ({ app }) => {
  const shots = app.featureShots || [];
  const [openIndex, setOpenIndex] = useState(null);
  const [imgIndex, setImgIndex] = useState(0);
  const [dir, setDir] = useState(0);
  const isOpen = openIndex !== null;
  const gallery = isOpen ? toGallery(shots[openIndex]) : [];
  const current = gallery[imgIndex] || app.image;
  const multi = gallery.length > 1;

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const openFeature = (i) => {
    if (toGallery(shots[i]).length) {
      setImgIndex(0);
      setDir(0);
      setOpenIndex(i);
    }
  };
  const close = () => setOpenIndex(null);
  const switchFeature = (i) => {
    setImgIndex(0);
    setDir(0);
    setOpenIndex(i);
  };
  const go = (step) => {
    if (!multi) return;
    setDir(step);
    setImgIndex((p) => (p + step + gallery.length) % gallery.length);
  };

  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2">
        {app.features.map((f, i) => {
          const hasShot = toGallery(shots[i]).length > 0;
          const count = toGallery(shots[i]).length;
          return (
            <button
              key={f}
              type="button"
              data-testid={`feature-btn-${i}`}
              onClick={() => openFeature(i)}
              disabled={!hasShot}
              className="group flex w-full items-center gap-4 rounded-2xl border border-border p-5 text-left transition-all hover:-translate-y-0.5 hover:border-navy/30 hover:shadow-md disabled:cursor-default disabled:hover:translate-y-0 disabled:hover:shadow-none"
            >
              <span
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors"
                style={{ backgroundColor: `${app.accent}18`, color: app.accent }}
              >
                <Check size={17} />
              </span>
              <span className="flex-1 font-medium text-navy">{f}</span>
              {count > 1 && (
                <span
                  className="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold"
                  style={{ backgroundColor: `${app.accent}18`, color: app.accent }}
                >
                  {count} écrans
                </span>
              )}
              {hasShot && (
                <ChevronRight
                  size={18}
                  className="shrink-0 text-navy/30 transition-transform group-hover:translate-x-0.5"
                  style={{ color: `${app.accent}99` }}
                />
              )}
            </button>
          );
        })}
      </div>
      <p className="pt-4 text-sm text-muted-foreground">
        👆 Cliquez sur une fonctionnalité pour l'ouvrir en grand. Faites défiler à gauche/droite pour voir plusieurs écrans.
      </p>

      {/* Vitrine plein écran */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            data-testid="feature-showcase"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[80] flex flex-col"
          >
            <img
              src={app.heroImage}
              alt=""
              aria-hidden
              className="absolute inset-0 h-full w-full object-cover blur-[3px]"
            />
            <div className="absolute inset-0 bg-navy/85" />
            <div
              className="absolute inset-0"
              style={{ background: `radial-gradient(120% 90% at 15% 20%, ${app.accent}40 0%, transparent 55%)` }}
            />

            {/* Barre du haut */}
            <div className="relative z-10 flex items-center justify-between gap-4 px-5 py-5 sm:px-8">
              <button
                type="button"
                onClick={close}
                data-testid="feature-showcase-back"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-medium text-white backdrop-blur-md transition-colors hover:bg-white/20"
              >
                <ArrowLeft size={16} /> Retour aux fonctionnalités
              </button>
              <button
                type="button"
                onClick={close}
                aria-label="Fermer"
                data-testid="feature-showcase-close"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white backdrop-blur-md transition-colors hover:bg-white/20"
              >
                <X size={18} />
              </button>
            </div>

            {/* Contenu */}
            <div className="relative z-10 flex flex-1 flex-col items-center justify-center gap-5 overflow-y-auto px-4 pb-4">
              <div className="text-center">
                <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3.5 py-1.5 text-sm font-medium text-white backdrop-blur-md">
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: app.accent }} />
                  {app.features[openIndex]}
                  {multi && (
                    <span className="text-white/60">
                      · {imgIndex + 1}/{gallery.length}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex w-full items-center justify-center gap-3 sm:gap-6">
                {multi && (
                  <button
                    type="button"
                    onClick={() => go(-1)}
                    aria-label="Écran précédent"
                    data-testid="feature-showcase-prev"
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/25 bg-white/10 text-white backdrop-blur-md transition-colors hover:bg-white/25"
                  >
                    <ChevronLeft size={22} />
                  </button>
                )}

                <div className="relative flex justify-center">
                  <AnimatePresence mode="wait" custom={dir}>
                    <motion.div
                      key={`${openIndex}-${imgIndex}`}
                      custom={dir}
                      initial={{ opacity: 0, x: dir === 0 ? 0 : dir > 0 ? 60 : -60, scale: 0.97 }}
                      animate={{ opacity: 1, x: 0, scale: 1 }}
                      exit={{ opacity: 0, x: dir > 0 ? -60 : 60, scale: 0.98 }}
                      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                      drag={multi ? "x" : false}
                      dragConstraints={{ left: 0, right: 0 }}
                      dragElastic={0.2}
                      onDragEnd={(e, info) => {
                        if (info.offset.x < -60) go(1);
                        else if (info.offset.x > 60) go(-1);
                      }}
                      className="relative cursor-grab overflow-hidden rounded-[2rem] border border-white/25 shadow-2xl active:cursor-grabbing"
                    >
                      <img
                        src={current}
                        alt={app.features[openIndex]}
                        draggable={false}
                        className="block max-h-[62vh] w-auto max-w-[78vw] select-none sm:max-w-[360px]"
                      />
                      <div className="pointer-events-none absolute inset-0 rounded-[2rem] ring-1 ring-inset ring-white/40" />
                    </motion.div>
                  </AnimatePresence>
                </div>

                {multi && (
                  <button
                    type="button"
                    onClick={() => go(1)}
                    aria-label="Écran suivant"
                    data-testid="feature-showcase-next"
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/25 bg-white/10 text-white backdrop-blur-md transition-colors hover:bg-white/25"
                  >
                    <ChevronRight size={22} />
                  </button>
                )}
              </div>

              {/* Points */}
              {multi && (
                <div className="flex items-center gap-2">
                  {gallery.map((_, gi) => (
                    <button
                      key={gi}
                      type="button"
                      aria-label={`Écran ${gi + 1}`}
                      data-testid={`feature-showcase-dot-${gi}`}
                      onClick={() => {
                        setDir(gi > imgIndex ? 1 : -1);
                        setImgIndex(gi);
                      }}
                      className="h-2 rounded-full transition-all"
                      style={{
                        width: gi === imgIndex ? 22 : 8,
                        backgroundColor: gi === imgIndex ? app.accent : "rgba(255,255,255,0.35)",
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Sélecteur d'autres fonctionnalités */}
            <div className="relative z-10 border-t border-white/10 bg-navy/40 px-5 py-4 backdrop-blur-md sm:px-8">
              <div className="mx-auto flex max-w-4xl flex-wrap justify-center gap-2">
                {app.features.map((f, i) => {
                  if (toGallery(shots[i]).length === 0) return null;
                  const active = i === openIndex;
                  return (
                    <button
                      key={f}
                      type="button"
                      onClick={() => switchFeature(i)}
                      data-testid={`feature-showcase-tab-${i}`}
                      className="rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors"
                      style={
                        active
                          ? { backgroundColor: app.accent, color: "#fff" }
                          : { backgroundColor: "rgba(255,255,255,0.10)", color: "rgba(255,255,255,0.75)" }
                      }
                    >
                      {f}
                    </button>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
